import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import { uniqBy } from 'lodash';
import { PermissionSet, Role, Team, User } from 'api/models';
import { RootState } from 'data';
import { Member, MembersResponse } from './api';

export interface MembersState extends MembersResponse {
  loading: boolean;
  error?: boolean;
  loadedAllMembers: boolean;
}

export interface InvitedMembers {
  user: { userId: number };
  permissionSet?: {
    permissionSetType: number;
    permissionSetId?: number;
  };
  active?: boolean;
  joinedAt?: number;
  role?: Role;
  referenceTeam?: Team;
  projectFinancialsBudgetIds?: number[];
}

export type RemovedMembers = Omit<
  InvitedMembers,
  'permissionSet' | 'active' | 'joinedAt'
>;

const INITIAL_STATE: MembersState = {
  users: [],
  teams: [],
  members: [],
  loadedAllMembers: false,
  loading: false,
  error: false,
};
const MembersSlice = createSlice({
  name: 'members',
  initialState: INITIAL_STATE,

  reducers: (create) => ({
    resetStatus: create.reducer((state) => {
      state.loadedAllMembers = false;
      state.error = false;
      state.loading = false;
    }),

    setMembers: create.reducer(
      (state: MembersState, { payload }: PayloadAction<MembersResponse>) => {
        const { users = [], teams = [], members } = payload;

        // get the existing store data
        const userListFromState = dotPropImmutable.get(state, 'users', []);
        const teamsListFromStore = dotPropImmutable.get(state, 'teams', []);
        const membersFromStore = dotPropImmutable.get(state, 'members', []);

        // set the unique values
        let result = dotPropImmutable.set(
          state,
          'users',
          uniqBy([...userListFromState, ...users], 'userId'),
        );

        result = dotPropImmutable.set(
          result,
          'teams',
          uniqBy([...teamsListFromStore, ...teams], 'teamId'),
        );

        result = dotPropImmutable.set(result, 'members', [
          ...membersFromStore,
          ...members,
        ]);

        return dotPropImmutable.set(result, 'loadedAllMembers', true);
      },
    ),

    addUser: create.reducer((state, { payload }: PayloadAction<User>) => {
      const userListFromState = dotPropImmutable.get(state, 'users', []);

      return dotPropImmutable.set(
        state,
        'users',
        uniqBy([...userListFromState, payload], 'userId'),
      );
    }),

    addUsers: create.reducer((state, { payload }: PayloadAction<User[]>) => {
      const userListFromState = dotPropImmutable.get(state, 'users', []);

      return dotPropImmutable.set(
        state,
        'users',
        uniqBy([...userListFromState, ...payload], 'userId'),
      );
    }),

    addMembers: create.reducer(
      (state, { payload }: PayloadAction<Member[]>) => {
        return dotPropImmutable.merge(state, 'members', payload);
      },
    ),

    updateMember: create.reducer(
      (
        state: MembersState,
        {
          payload,
        }: PayloadAction<{
          user: AtLeast<User, 'userId'>;
          permissionSet?: PermissionSet;
        }>,
      ) => {
        const { user, permissionSet } = payload;

        const members: Member[] = dotPropImmutable.get(state, 'members', []);

        const updatedMembers = members.map((member) => {
          return {
            ...member,
            user:
              member.user.userId === user.userId
                ? { ...member.user, ...user }
                : member.user,
            permissionSet:
              permissionSet && member.user.userId === user.userId
                ? { ...member.permissionSet, ...permissionSet }
                : member.permissionSet,
          };
        });

        return dotPropImmutable.set(state, 'members', updatedMembers);
      },
    ),

    setMembersLoading: create.reducer(
      (state: MembersState, action: PayloadAction<boolean>) => {
        state.loading = action.payload;
        state.error = false;
      },
    ),

    setMembersError: create.reducer(
      (state: MembersState, action: PayloadAction<boolean>) => {
        state.error = action.payload;
        state.loading = false;
      },
    ),

    setAllMembersSucess: create.reducer(
      (state: MembersState, action: PayloadAction<boolean>) => {
        state.loadedAllMembers = action.payload;
        state.error = false;
        state.loading = false;
      },
    ),
  }),
});

export const selectMembers = (state: RootState): MembersState => state.members;
export const selectUsersList = (state: RootState): User[] =>
  state.members.users ?? [];

export const selectUser =
  (id: number) =>
  ({ members }: RootState): User | undefined =>
    members.users?.find((user) => user.userId === id);

export const {
  setMembers,
  addUser,
  addUsers,
  addMembers,
  setMembersLoading,
  setMembersError,
  updateMember,
} = MembersSlice.actions;
export const membersReducer = MembersSlice.reducer;
